import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import { PaginatedResponse } from "@/interfaces/paginatedResponse";
import axios from "@/lib/axios";
import { useQuery } from "@tanstack/vue-query";
import { useDebounceFn } from "@vueuse/core";
import { reactive, ref, watch } from "vue";
import { HotelGroups } from "../../../../interfaces/HotelGroup";

interface Props {
  immediate?: boolean;
  queryKey?: string;
  errorHandling?: boolean;
}

interface Params {
  page: number | string;
  query: string;
  filters?: Filters;
}

interface Filters {
  orderBy?: string;
  orderDirection?: string;
  active?: number | string;
}

const getHotelGroups = async (
  params: Params,
  errorHandling = true,
): Promise<PaginatedResponse<HotelGroups>> => {
  const { data } = await axios.get(
    `/api/internal/hotel-groups/search?q=${params.query}&page=${params.page}`,
    {
      errorHandling: errorHandling,
      params: params.filters
        ? {
            orderBy: params.filters.orderBy,
            orderDirection: params.filters.orderDirection,
            active:
              params.filters.active !== "Todos"
                ? params.filters.active
                : undefined,
          }
        : undefined,
    } as CustomAxiosRequestConfig,
  );

  return data;
};

const useSearchHotelGroups = (props: Props) => {
  const { immediate = true, errorHandling } = props;
  const page = ref<number | string>(1);
  const query = ref<string>("");

  const filters = reactive<Filters>({
    orderBy: "name",
    orderDirection: "asc",
    active: undefined,
  });

  const { data, isError, isLoading, refetch } = useQuery(
    ["hotelGroups", props.queryKey],
    () =>
      getHotelGroups(
        {
          page: page.value,
          query: query.value,
          filters: filters,
        },
        errorHandling,
      ),
    {
      enabled: immediate,
    },
  );

  const setPage = (n: number | string) => {
    page.value = n;
  };

  const setQuery = (q: string) => {
    query.value = q;
    page.value = 1;
  };

  const debouncedSetQuery = useDebounceFn((e) => {
    setQuery(e);
  }, 500);

  watch(query, () => {
    if (immediate) {
      refetch();
    }
  });

  watch(filters, () => {
    if (immediate) {
      refetch();
    }
  });

  return {
    data,
    page,
    filters,
    isError,
    isLoading,
    refetch,
    setQuery,
    setPage,
    debouncedSetQuery,
  };
};

export default useSearchHotelGroups;
